/**
 * "Dealer plays for double" point transfers for a scored hand — the standard
 * Chinese Classical convention (Ron: the discarder pays double if either
 * side is dealer; Tsumo: everyone pays double to a dealer winner, and the
 * dealer alone pays double to a non-dealer winner). Pure Seat/number
 * arithmetic, no dependency on live game state; split out of
 * `advanceMatch.ts` for the 200-line cap.
 */

import { SEAT_COUNT, type Seat } from '../game/types.js';
import type { HandResult } from '../game/stateCore.js';
import type { HandScore } from '../scoring/types.js';

/** Ron: dealer-involved payments are doubled. Tsumo: the dealer's own share (as payer or winner) is doubled. */
const DEALER_PAYMENT_MULTIPLIER = 2;

function transfer(
  scores: Readonly<Record<Seat, number>>,
  payer: Seat,
  payee: Seat,
  amount: number,
): Readonly<Record<Seat, number>> {
  return { ...scores, [payer]: scores[payer] - amount, [payee]: scores[payee] + amount };
}

/** Ron: the discarder pays double if either they or the winner is the dealer, else the plain amount. */
function applyRonPayment(
  scores: Readonly<Record<Seat, number>>,
  dealer: Seat,
  winner: Seat,
  from: Seat,
  points: number,
): Readonly<Record<Seat, number>> {
  const dealerInvolved = winner === dealer || from === dealer;
  return transfer(scores, from, winner, dealerInvolved ? points * DEALER_PAYMENT_MULTIPLIER : points);
}

/**
 * Tsumo: every other seat pays the winner. Whichever of {this payer, the
 * winner} is the dealer doubles that payer's share — covers both "dealer
 * wins" (everyone pays double) and "dealer pays a non-dealer winner double
 * while the other two pay single" with one rule.
 */
function applyTsumoPayment(
  scores: Readonly<Record<Seat, number>>,
  dealer: Seat,
  winner: Seat,
  points: number,
): Readonly<Record<Seat, number>> {
  let next = scores;
  for (let s = 0; s < SEAT_COUNT; s++) {
    const payer = s as Seat;
    if (payer === winner) continue;
    const amount = payer === dealer || winner === dealer ? points * DEALER_PAYMENT_MULTIPLIER : points;
    next = transfer(next, payer, winner, amount);
  }
  return next;
}

/** Applies a scored hand's payments to `scores`; a `null` handScore (unscored ruleset) or a drawn hand is a no-op. */
export function applyHandPayments(
  scores: Readonly<Record<Seat, number>>,
  dealer: Seat,
  result: HandResult,
  handScore: HandScore | null,
): Readonly<Record<Seat, number>> {
  if (handScore === null || result.kind === 'exhausted') return scores;
  return result.kind === 'ron'
    ? applyRonPayment(scores, dealer, result.winner, result.from, handScore.points)
    : applyTsumoPayment(scores, dealer, result.winner, handScore.points);
}
